import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/ledger/Navbar";
import ScrollProgress from "@/components/ledger/ScrollProgress";
import Footer from "@/components/ledger/Footer";
import FloatingWhatsApp from "@/components/velora/FloatingWhatsApp";
import { INFO_PAGES, buildInfoPageJsonLd } from "./infoPages.mjs";

const SITE = "https://www.viloraimpex.com";

export default function InfoPage({ slug }) {
  const cfg = INFO_PAGES[slug];

  useSeo({
    title: cfg?.seoTitle || "Vilora Impex",
    description: cfg?.seoDescription,
    path: cfg ? `/${cfg.slug}` : "/",
    image: `${SITE}/og.jpg`,
    imageAlt: cfg?.h1,
    jsonLd: cfg ? buildInfoPageJsonLd(slug) : null,
  });

  if (!cfg) {
    return (
      <main className="ledger info-page relative min-h-screen">
        <Navbar />
        <section className="paper" style={{ padding: "80px 24px" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <h1 className="display" style={{ fontSize: 40 }}>Page not found</h1>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main className="ledger info-page relative min-h-screen" data-testid="info-page">
      <InfoPageStyles />
      <ScrollProgress />
      <Navbar />

      <section className="info-page-hero paper">
        <div className="info-page-shell">
          <nav className="info-page-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">·</span>
            <span aria-current="page">{cfg.h1}</span>
          </nav>

          <div className="info-page-folio">
            <span className="folio">{cfg.folio}</span>
            <span className="rule rule-grow" />
            <span className="folio" style={{ color: "var(--ink-mute)" }}>{cfg.sectionLabel}</span>
          </div>

          <span className="brass-ribbon info-page-eyebrow">{cfg.eyebrow}</span>
          <h1 className="display info-page-h1">{cfg.h1}</h1>
          <p className="info-page-standfirst">{cfg.standfirst}</p>
        </div>
      </section>

      {cfg.facts?.length ? (
        <section className="info-page-facts paper">
          <div className="info-page-shell">
            <dl className="info-page-facts-grid">
              {cfg.facts.map((f) => (
                <div key={f.label} className="info-page-fact">
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      ) : null}

      <section className="info-page-body paper">
        <div className="info-page-shell">
          {cfg.sections.map((s, i) => (
            <div key={i} className="info-page-section">
              <h2 className="display">{s.h2}</h2>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="info-page-cta paper">
        <div className="info-page-shell">
          <h2 className="display">Talk to the export desk</h2>
          <p className="info-page-cta-body">
            Tell us your spec, destination port, packing preference and quantity. We respond the
            same day in our timezone with price, timeline and the documents we'll need from you.
          </p>
          <div className="info-page-actions">
            <a href="mailto:exports@viloraimpex.com" className="btn-primary">
              <span className="btn-code">DESK</span>
              <span>exports@viloraimpex.com</span>
              <span className="btn-arrow"><Mail size={14} strokeWidth={1.6} /></span>
            </a>
            <Link to="/" className="btn-ghost">
              <ArrowLeft size={14} strokeWidth={1.6} />
              <span>Back to Vilora Impex</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}

function InfoPageStyles() {
  return (
    <style>{`
      .info-page { color: var(--ink); }
      .info-page-shell { max-width: 880px; margin: 0 auto; padding: 0 clamp(20px, 4vw, 40px); }
      .info-page-hero { padding: clamp(48px, 6vw, 80px) 0 clamp(28px, 4vw, 48px); }
      .info-page-breadcrumb {
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--ink-mute);
        display: flex; gap: 10px; align-items: center;
        margin-bottom: 18px;
      }
      .info-page-breadcrumb a { color: var(--brass-deep); text-decoration: none; }
      .info-page-folio { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
      .info-page-eyebrow { display: inline-block; margin-bottom: 18px; }
      .info-page-h1 {
        font-size: clamp(36px, 5.4vw, 64px);
        line-height: 1.02;
        margin: 0 0 18px;
        letter-spacing: -0.005em;
      }
      .info-page-standfirst {
        font-family: 'Crimson Pro', Georgia, serif;
        font-size: clamp(18px, 1.8vw, 22px);
        line-height: 1.55;
        color: var(--ink-soft);
        max-width: 760px;
        margin: 0;
      }
      .info-page-facts {
        padding: clamp(28px, 4vw, 44px) 0;
        background: linear-gradient(180deg, rgba(168,132,61,0.06), rgba(168,132,61,0)), var(--paper-1, #FAF6EA);
      }
      .info-page-facts-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 20px 32px;
        margin: 0;
      }
      .info-page-fact {
        border-left: 2px solid var(--brass);
        padding-left: 14px;
      }
      .info-page-fact dt {
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--brass-deep);
        margin-bottom: 4px;
      }
      .info-page-fact dd {
        font-family: 'Crimson Pro', serif;
        font-size: 16.5px;
        line-height: 1.45;
        color: var(--ink);
        margin: 0;
      }
      .info-page-body { padding: clamp(36px, 5vw, 56px) 0; }
      .info-page-section { margin-bottom: 28px; }
      .info-page-section:last-child { margin-bottom: 0; }
      .info-page-section h2 {
        font-family: 'Cormorant Garamond', serif;
        font-weight: 700;
        font-size: clamp(24px, 2.8vw, 32px);
        line-height: 1.1;
        color: var(--ink);
        margin: 0 0 12px;
      }
      .info-page-section p {
        font-family: 'Crimson Pro', Georgia, serif;
        font-size: clamp(17px, 1.5vw, 19px);
        line-height: 1.68;
        color: var(--ink-soft);
        margin: 0;
      }
      .info-page-cta {
        padding: clamp(36px, 5vw, 56px) 0 clamp(48px, 6vw, 80px);
        background: linear-gradient(180deg, rgba(168,132,61,0.04), rgba(168,132,61,0)), var(--paper-2, #ECE3CE);
      }
      .info-page-cta h2 {
        font-size: clamp(28px, 3.2vw, 38px);
        margin: 0 0 16px;
      }
      .info-page-cta-body {
        font-family: 'Crimson Pro', serif;
        font-size: clamp(17px, 1.5vw, 19px);
        line-height: 1.65;
        color: var(--ink-soft);
        margin: 0 0 22px;
        max-width: 720px;
      }
      .info-page-actions { display: flex; flex-wrap: wrap; gap: 12px; }
    `}</style>
  );
}
